/**
 * The AI assistant's filters ({ key, sign, value, valueMax, option }, see the backend's ScanFilterCatalog) to and
 * from the scan table's own formats: the request payload (what is on screen) and the URL query that applyQuery()
 * reads (how a saved scan or a shared link is applied).
 */

// A plain decimal string: applyQuery() only reads digits, never "5e+10".
function plainNumber(n) {
  if (Number.isInteger(n)) return n.toFixed(0);
  const s = String(n);
  return /e/i.test(s) ? n.toFixed(10).replace(/\.?0+$/, "") : s;
}

/**
 * The All Stocks query applyQuery() turns into exactly these filters (it replaces every filter on screen), with the
 * assistant's order and rows per page: no sort is the page's default order, no rows keeps the rows on screen, rows 0
 * is "all".
 */
export function filtersToQuery(filters, { sort = null, rows = null } = {}) {
  const query = { scan: "universe" };
  if (sort) {
    query.sort = sort.column;
    if (sort.order === "desc") query.order = "desc";
  }
  if (rows != null) query.rows = rows === 0 ? "all" : String(rows);
  filters.forEach((f) => {
    if (f.option != null) query[f.key] = f.option;
    else if (f.sign == null) query[f.key] = "1";
    else if (f.sign === "between") query[f.key] = `${plainNumber(f.value)}..${plainNumber(f.valueMax)}`;
    else if (f.sign === ">=") query[f.key] = `${plainNumber(f.value)}..`;
    else if (f.sign === "<=") query[f.key] = `..${plainNumber(f.value)}`;
    else query[f.key] = `${f.sign}${plainNumber(f.value)}`;
  });
  return query;
}

/**
 * A date filter of the payload ({ sign, value: "YYYY-MM-DD", valueMax }) as the assistant's option, which is also
 * the URL form applyQuery() reads: 2026-10-13, >2026-10-13, <2026-10-13, 2026-10-13..2026-10-17, 2026-10-13.., ..2026-10-17.
 */
function dayOption({ sign, value, valueMax }) {
  if (sign === "between") return `${value}..${valueMax}`;
  if (sign === ">=") return `${value}..`;
  if (sign === "<=") return `..${value}`;
  return sign === "=" ? value : `${sign}${value}`;
}

/** The filters on screen, from ScanTable's buildPayload(). */
export function filtersFromPayload(payload) {
  const filters = [];
  Object.keys(payload).forEach((key) => {
    if (key === "scanName" || key === "date") return;
    const v = payload[key];
    if (v === true) filters.push({ key });
    else if (typeof v === "string") filters.push({ key, option: v });
    else if (v && typeof v === "object" && typeof v.value === "string") filters.push({ key, option: dayOption(v) });
    else if (v && typeof v === "object" && typeof v.value === "number") {
      filters.push({ key, sign: v.sign, value: v.value, valueMax: v.valueMax ?? null });
    }
  });
  return filters;
}
